import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { DomainBoundary } from './routing/DomainBoundary';
import { domains } from './routing/domains';
import DashboardLayout from './components/DashboardLayout';

// Lazy-loaded feature pages
const LandingPage = lazy(() => import('./features/landing/LandingPage'));
const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const SignUpPage = lazy(() => import('./features/auth/SignUpPage'));
const DashboardHome = lazy(() => import('./features/dashboard/DashboardHome'));
const PortfolioOverview = lazy(() => import('./features/portfolio/PortfolioOverview'));
const InstrumentDetailPage = lazy(() => import('./features/instruments/InstrumentDetailPage'));
const TradeHistory = lazy(() => import('./features/trades/TradeHistory'));
const TradePage = lazy(() => import('./features/trades/TradePage'));
const NotificationPrefs = lazy(() => import('./features/notifications/NotificationPrefs'));
const NotificationFeed = lazy(() => import('./features/notifications/NotificationFeed'));
const DailyReportSettings = lazy(() => import('./features/notifications/DailyReportSettings'));
const PendingApprovals = lazy(() => import('./features/approvals/PendingApprovals'));
const PositionManagement = lazy(() => import('./features/positions/PositionManagement'));
const ConfigSettings = lazy(() => import('./features/config/ConfigSettings'));
const ProfilePage = lazy(() => import('./features/profile/ProfilePage'));
const ChangePassword = lazy(() => import('./features/profile/ChangePassword'));
const MfaSetup = lazy(() => import('./features/profile/MfaSetup'));
const ForgotPassword = lazy(() => import('./features/profile/ForgotPassword'));
const BrokerList = lazy(() => import('./features/brokers/BrokerList'));
const ConnectBrokerForm = lazy(() => import('./features/brokers/ConnectBrokerForm'));
const GettingStarted = lazy(() => import('./features/guide/GettingStarted'));
const HelpSection = lazy(() => import('./features/guide/HelpSection'));
const MyStrategiesPage = lazy(() => import('./features/strategies/MyStrategiesPage'));
const StrategyForm = lazy(() => import('./features/strategies/StrategyForm'));
const PlatformMessagesPage = lazy(() => import('./features/messages/PlatformMessagesPage'));
const StatusPage = lazy(() => import('./features/status/StatusPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center p-12">
      <p className="text-gray-500">Loading…</p>
    </div>
  );
}

function NotFound() {
  return <div className="p-6"><h1 className="text-xl">404 — Page not found</h1></div>;
}

export default function App() {
  return (
    <DomainBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* The public home remains readable even when the visitor is signed in. */}
          {!domains().isDashboard && <Route path="/" element={<LandingPage />} />}

          {/* Public routes (no layout shell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/health" element={<Navigate to="/status" replace />} />

          {/* Protected routes with dashboard layout */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              {domains().isDashboard && <Route path="/" element={<DashboardHome />} />}
              <Route path="/dashboard" element={<DashboardHome />} />
              <Route path="/portfolio" element={<PortfolioOverview />} />
              <Route path="/instruments/:symbol" element={<InstrumentDetailPage />} />
              <Route path="/trades" element={<TradeHistory />} />
              <Route path="/trade" element={<TradePage />} />
              <Route path="/notifications" element={<NotificationFeed />} />
              <Route path="/notifications/preferences" element={<NotificationPrefs />} />
              <Route path="/notifications/daily-report" element={<DailyReportSettings />} />
              <Route path="/messages" element={<PlatformMessagesPage />} />
              <Route path="/approvals" element={<PendingApprovals />} />
              <Route path="/positions" element={<PositionManagement />} />
              <Route path="/config" element={<ConfigSettings />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/change-password" element={<ChangePassword />} />
              <Route path="/profile/mfa" element={<MfaSetup />} />
              <Route path="/brokers" element={<BrokerList />} />
              <Route path="/brokers/connect" element={<ConnectBrokerForm />} />
              <Route path="/brokers/:brokerId/update" element={<ConnectBrokerForm />} />
              <Route path="/strategies" element={<MyStrategiesPage />} />
              <Route path="/strategies/create" element={<StrategyForm />} />
              <Route path="/strategies/:id/edit" element={<StrategyForm />} />
              <Route path="/indicators" element={<Navigate to="/strategies?type=indicator" replace />} />
              <Route path="/guide" element={<GettingStarted />} />
              <Route path="/help" element={<HelpSection />} />
            </Route>
          </Route>

          {/* Fallback */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Suspense>
    </DomainBoundary>
  );
}
